import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Drawer, FormInstance, Space, Tabs } from 'antd';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../api/useAPIQuery';
import ErrorResult from '../components/ErrorResult';
import PageTitle from '../components/PageTitle';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { CandidateId, JobPostingId } from '../types';
import styles from './ApplicantNavigator/ApplicantNavigator.module.css';

const EditApplicantNavigator = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const activeTabKey = location.pathname.replace(/\/$/, '').split('/').pop() || 'details';
  const [searchParams] = useSearchParams();
  const navigate = useTypedNavigate();
  const { tenant, id, applicant_id } = useTypedParams({
    tenant: String,
    id: JobPostingId,
    applicant_id: CandidateId,
  });

  const [drawerForm, setDrawerForm] = useState<FormInstance | null>(null);
  const [drawerMutationIsLoading, setDrawerMutationIsLoading] = useState(false);

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const onClose = () => {
    switch (activeTabKey) {
      case 'attachments':
        if (id) {
          navigate({
            pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/attachments/',
            params: { tenant, id, applicant_id },
            search: searchParams,
          });
        } else {
          navigate({
            pathname: '/:tenant/talent-pool/candidates/:applicant_id/attachments/',
            params: { tenant, applicant_id },
            search: searchParams,
          });
        }
        break;
      case 'details':
      default:
        if (id) {
          navigate({
            pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/details/',
            params: { tenant, id, applicant_id },
            search: searchParams,
          });
        } else {
          navigate({
            pathname: '/:tenant/talent-pool/candidates/:applicant_id/details/',
            params: { tenant, applicant_id },
            search: searchParams,
          });
        }
        break;
    }
  };

  const editPageItems = id
    ? [
        { key: 'details', label: t('Dados') },
        { key: 'attachments', label: t('Anexos') },
        { key: 'interviews', label: t('Entrevistas') },
      ]
    : [
        { key: 'details', label: t('Dados') },
        { key: 'attachments', label: t('Anexos') },
      ];

  return (
    <Drawer
      open
      size="large"
      className={styles.drawer}
      closable={false}
      maskClosable
      onClose={onClose}
      title={
        <PageTitle
          title={candidate.data?.name || '...'}
          level={2}
          extra={[
            <Button key="close" type="text" onClick={onClose} shape="circle">
              <CloseOutlined />
            </Button>,
          ]}
          footer={
            <Tabs
              activeKey={activeTabKey}
              onChange={(key) => {
                setDrawerForm(null);

                switch (key) {
                  case 'details':
                    if (id) {
                      navigate(
                        {
                          pathname:
                            '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/details/',
                          params: { tenant, id, applicant_id },
                          search: searchParams,
                        },
                        { replace: true }
                      );
                    } else {
                      navigate(
                        {
                          pathname: '/:tenant/talent-pool/candidates/:applicant_id/change/details/',
                          params: { tenant, applicant_id },
                          search: searchParams,
                        },
                        { replace: true }
                      );
                    }
                    break;
                  case 'attachments':
                    if (id) {
                      navigate(
                        {
                          pathname:
                            '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/attachments/',
                          params: { tenant, id, applicant_id },
                          search: searchParams,
                        },
                        { replace: true }
                      );
                    } else {
                      navigate(
                        {
                          pathname:
                            '/:tenant/talent-pool/candidates/:applicant_id/change/attachments/',
                          params: { tenant, applicant_id },
                          search: searchParams,
                        },
                        { replace: true }
                      );
                    }
                    break;
                  case 'interviews':
                    if (id) {
                      navigate(
                        {
                          pathname:
                            '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/change/interviews/',
                          params: { tenant, id, applicant_id },
                          search: searchParams,
                        },
                        { replace: true }
                      );
                    }
                    break;
                }
              }}
              items={editPageItems}
            />
          }
        />
      }
      footer={
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={drawerMutationIsLoading}
            onClick={() => drawerForm?.submit()}
            data-testid="edit-candidate-save-button"
          >
            {' '}
            <Trans>Salvar</Trans>
          </Button>
          <Button onClick={onClose}>
            <Trans>Fechar</Trans>
          </Button>
        </Space>
      }
    >
      <ErrorResult error={candidate.error}>
        <Outlet
          context={{
            setDrawerForm,
            setDrawerMutationIsLoading,
          }}
        />
      </ErrorResult>
    </Drawer>
  );
};

export default EditApplicantNavigator;
